/**
 * Situation Awareness Tab — The "command center" view
 *
 * At-a-glance operational picture: threat metrics, activity timeline,
 * trend summary, and classification breakdown. All wired to real data.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldExclamationIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  intelligenceApi,
  type TimeRange,
  getPreviousTimeWindow,
  getGranularity,
} from '../../services/analyticsService';
import { tracksApi } from '../../services/api';
import { reportsApi } from '../../services/reportsApi';
import { useAnimatedCounter } from '../../hooks/useAnimatedCounter';
import { SkeletonCard, SkeletonChart } from '../common/Skeleton';
import { format } from 'date-fns';
import { useUnitsStore } from '../../store/unitsStore';
import { convertAltitude, altitudeUnit } from '../../utils/units';

// CLASSIFICATION_COLORS dropped in Phase 8 — the by-classification charts
// were replaced with by-make. If a future widget wants the old palette
// back, lift it from git history (e.g. `git show HEAD~5:.../OverviewTab.tsx`).

// Per-manufacturer palette for the by-make charts. Brand-adjacent colors
// where there's an obvious one (DJI red, Autel orange, Skydio blue,
// Parrot green, Yuneec purple); everything else falls through to a
// modulo-cycled palette so unknown makes still get a consistent color
// from one render to the next.
const MAKE_COLORS: Record<string, string> = {
  DJI: '#e63b4d',
  AUTEL: '#ffb547',
  SKYDIO: '#5fd5ff',
  PARROT: '#3fc88c',
  YUNEEC: '#a855f7',
  ANZU: '#ffce6b',
  TELEDYNE: '#5ea3ff',
  HOLY_STONE: '#c084fc',
  UNKNOWN: '#6b7280',
};
const MAKE_FALLBACK_PALETTE = [
  '#ffd166', '#ff9f43', '#f48a4a', '#c084fc', '#5ea3ff', '#3fc88c',
];
function colorForMake(make: string): string {
  if (MAKE_COLORS[make]) return MAKE_COLORS[make];
  // Stable hash so the same make always gets the same fallback color.
  let h = 0;
  for (let i = 0; i < make.length; i++) h = (h * 31 + make.charCodeAt(i)) | 0;
  return MAKE_FALLBACK_PALETTE[Math.abs(h) % MAKE_FALLBACK_PALETTE.length];
}

function AnimatedMetric({ value, suffix }: { value: number; suffix?: string }) {
  const animated = useAnimatedCounter(value, 1000);
  return (
    <span className="tabular-nums">
      {animated.toLocaleString()}{suffix}
    </span>
  );
}

interface OverviewTabProps {
  tenantId: string;
  timeRange: TimeRange;
  startTime: string;
  endTime: string;
  analyticsAvailable: boolean | null;
}

export default function OverviewTab({
  tenantId,
  timeRange,
  startTime,
  endTime,
  analyticsAvailable,
}: OverviewTabProps) {
  const { unitSystem } = useUnitsStore();

  // Reports summary is the canonical source of truth for the "Total
  // Detections" KPI — it uses the flight-level CTE shared with the
  // Dashboard, so the numbers match between the two pages. Falling back
  // to the Python analytics service's `unique_tracks` (below) means
  // we'd report a different definition of detections than the dashboard,
  // which is what operators flagged.
  const { data: reportsSummary } = useQuery({
    queryKey: ['analytics-reports-summary', startTime, endTime],
    queryFn: () =>
      reportsApi.getSummary({ from: startTime, to: endTime }),
    staleTime: 60 * 1000,
  });

  // Previous-window summary for trend percentages on the headline.
  // prevWindow is declared once here and reused for the legacy stats
  // query below.
  const prevWindow = getPreviousTimeWindow(timeRange);
  const { data: reportsSummaryPrev } = useQuery({
    queryKey: ['analytics-reports-summary-prev', timeRange],
    queryFn: () =>
      reportsApi.getSummary({
        from: prevWindow.startTime,
        to: prevWindow.endTime,
      }),
    staleTime: 60 * 1000,
  });

  // Alerts in the window — used to derive the rule-based metric tiles
  // (FAA altitude violations, geofence breaches). Same fetch the ThreatTab
  // uses; we just count distinct trackIds per rule type instead of
  // grouping into cards.
  const { data: alertsPage } = useQuery({
    queryKey: ['analytics-overview-alerts', startTime, endTime],
    queryFn: () =>
      reportsApi.listAlerts({ from: startTime, to: endTime }, 1, 100),
    staleTime: 60 * 1000,
  });
  const { data: alertsPagePrev } = useQuery({
    queryKey: ['analytics-overview-alerts-prev', timeRange],
    queryFn: () =>
      reportsApi.listAlerts(
        { from: prevWindow.startTime, to: prevWindow.endTime },
        1,
        100,
      ),
    staleTime: 60 * 1000,
  });

  // Count distinct flagged flights per rule. Matches ThreatTab's
  // classifier — ceiling violations come from AIRSPACE_INTRUSION alerts
  // whose title mentions 'ceiling' or '400 ft' (until metadata.subtype
  // is plumbed through the row shape). Geofence breaches are the alert
  // type GEOFENCE_BREACH directly.
  const ruleCounts = useMemo(() => {
    const seenCeiling = new Set<string>();
    const seenGeofence = new Set<string>();
    for (const a of alertsPage?.data ?? []) {
      if (!a.trackId) continue;
      if (a.type === 'GEOFENCE_BREACH') seenGeofence.add(a.trackId);
      else if (a.type === 'AIRSPACE_INTRUSION') {
        const t = a.title.toLowerCase();
        if (t.includes('ceiling') || t.includes('400 ft')) seenCeiling.add(a.trackId);
      }
    }
    return { ceiling: seenCeiling.size, geofence: seenGeofence.size };
  }, [alertsPage]);

  const ruleCountsPrev = useMemo(() => {
    const seenCeiling = new Set<string>();
    const seenGeofence = new Set<string>();
    for (const a of alertsPagePrev?.data ?? []) {
      if (!a.trackId) continue;
      if (a.type === 'GEOFENCE_BREACH') seenGeofence.add(a.trackId);
      else if (a.type === 'AIRSPACE_INTRUSION') {
        const t = a.title.toLowerCase();
        if (t.includes('ceiling') || t.includes('400 ft')) seenCeiling.add(a.trackId);
      }
    }
    return { ceiling: seenCeiling.size, geofence: seenGeofence.size };
  }, [alertsPagePrev]);

  // Drive the skeleton loader off the reports summary query — that's
  // the data source for the headline tile and the metric values, so it
  // gates the page load. The analytics-service stats query was removed
  // along with the Unclassified / Anomalies tiles in this rev.
  const statsLoading = !reportsSummary;

  // Fetch trend analysis
  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['intelligence', 'trends', tenantId, timeRange],
    queryFn: async () => {
      if (!analyticsAvailable || !tenantId) return null;
      try {
        return await intelligenceApi.getTrends(tenantId, startTime, endTime, getGranularity(timeRange));
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // By-make breakdown — pulled directly from tracks regardless of the
  // analytics service's availability, because manufacturer aggregation
  // is cheap to compute client-side and we want it on this page even
  // when the Python service is down. Phase 8 replaces the prior
  // by_classification charts with by_make.
  const { data: byMake } = useQuery({
    queryKey: ['analytics', 'by-make', startTime, endTime],
    queryFn: async (): Promise<Record<string, number>> => {
      // pageSize is server-side-capped at 1000 by paginationSchema. The
      // prior value of 10_000 was silently rejected by Zod validation —
      // the query came back with no `data` array which is why both the
      // by-make bar chart and the make-distribution pie sat empty.
      const res = await tracksApi.getTracks({ startTime, endTime, pageSize: 1000 });
      const tracks = (res.data ?? []) as Array<{ manufacturer?: string | null }>;
      const counts: Record<string, number> = {};
      for (const t of tracks) {
        // Normalize: trim, title-case, fall back to "Unknown" so the
        // pie + bar chart show something even for drones without an
        // identified manufacturer.
        const raw = (t.manufacturer ?? '').trim();
        const make = raw ? raw.toUpperCase() : 'UNKNOWN';
        counts[make] = (counts[make] ?? 0) + 1;
      }
      return counts;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Compute trend percentages
  function trendPct(current: number, previous: number | undefined | null): { pct: string; up: boolean } {
    if (!previous || previous === 0) return { pct: 'N/A', up: true };
    const change = ((current - previous) / previous) * 100;
    return { pct: `${change > 0 ? '+' : ''}${change.toFixed(0)}%`, up: change >= 0 };
  }

  // Headline detection count comes from reportsSummary, which uses the
  // same flight-level CTE as the Dashboard so the numbers match across
  // pages. Falls back to 0 while the query is in flight.
  const totalDetections = reportsSummary?.kpis.totalAlerts ?? 0;
  const totalDetectionsPrev = reportsSummaryPrev?.kpis.totalAlerts ?? 0;
  const totalTrend = trendPct(totalDetections, totalDetectionsPrev);
  const ceilingTrend = trendPct(ruleCounts.ceiling, ruleCountsPrev.ceiling);
  const breachTrend = trendPct(ruleCounts.geofence, ruleCountsPrev.geofence);

  // Timeline chart data from trends
  // Altitude is mapped to null (not 0) for quiet periods so the line draws
  // gaps instead of plummeting to the floor — 0 ft isn't the same as "no data".
  const timelineData = useMemo(() => {
    if (trendData?.data_points?.length) {
      return trendData.data_points.map((dp) => ({
        time: timeRange === '24h'
          ? format(new Date(dp.period), 'HH:mm')
          : format(new Date(dp.period), 'MM/dd'),
        tracks: dp.unique_tracks,
        altitude:
          dp.avg_altitude != null
            ? convertAltitude(dp.avg_altitude, unitSystem)
            : null,
      }));
    }
    return [];
  }, [trendData, timeRange, unitSystem]);

  // Pie chart data — by manufacturer (phase 8). The Classification
  // palette is kept around for any future chart that wants it, but the
  // breakdown charts on this tab are now Make-driven.
  const pieData = useMemo(() => {
    const source = byMake ?? {};
    return Object.entries(source).map(([name, value]) => ({
      name,
      value,
      color: colorForMake(name),
    }));
  }, [byMake]);

  // Metric cards. Three tiles: Total Detections leads (the headline
  // operators land on first), then the two rule-based metrics that map
  // 1:1 with the ThreatTab rules so the operator sees the same numbers
  // across both pages. Unclassified / Anomalies / by-source tiles were
  // dropped — they didn't carry their weight on the headline strip.
  const metrics = [
    {
      icon: ChartBarIcon,
      label: 'Total Detections',
      // Sourced from reportsApi.getSummary (flight-level CTE) so this
      // matches the Dashboard's number exactly.
      value: totalDetections,
      color: 'text-accent',
      bg: 'bg-accent/10',
      trend: totalTrend,
    },
    {
      icon: ArrowTrendingUpIcon,
      label: 'FAA Altitude Violations',
      // Distinct flights with at least one AIRSPACE_INTRUSION alert
      // whose title indicates a ceiling violation (matches the
      // ThreatTab classifier).
      value: ruleCounts.ceiling,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      trend: ceilingTrend,
    },
    {
      icon: ShieldExclamationIcon,
      label: 'Geofence Breaches',
      // Distinct flights with at least one GEOFENCE_BREACH alert.
      value: ruleCounts.geofence,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      trend: breachTrend,
    },
  ];

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <SkeletonChart />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Threat Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {metrics.map((m, i) => (
          <div key={i} className="glass-card p-5">
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${m.bg}`}>
                <m.icon className={`h-5 w-5 ${m.color}`} />
              </div>
              {m.trend.pct && (
                <span
                  className={`flex items-center text-xs font-semibold ${
                    m.trend.up ? 'text-red-400' : 'text-accent'
                  }`}
                >
                  {m.trend.up ? (
                    <ArrowTrendingUpIcon className="h-3.5 w-3.5 mr-0.5" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-3.5 w-3.5 mr-0.5" />
                  )}
                  {m.trend.pct}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-1">{m.label}</p>
            <p className="text-2xl font-display font-bold text-white">
              <AnimatedMetric value={m.value} />
            </p>
          </div>
        ))}
      </div>

      {/* Activity Trend Summary */}
      {trendData?.summary && (
        <div className="glass-card p-4 flex items-center gap-3">
          {trendData.summary.trend === 'increasing' ? (
            <ArrowTrendingUpIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
          ) : (
            <ArrowTrendingDownIcon className="h-5 w-5 text-accent flex-shrink-0" />
          )}
          <p className="text-sm text-gray-300">
            Activity is{' '}
            <span
              className={`font-semibold ${
                trendData.summary.trend === 'increasing' ? 'text-red-400' : 'text-accent'
              }`}
            >
              {trendData.summary.trend}
            </span>{' '}
            &mdash; {totalDetections.toLocaleString()} detections
            {totalDetectionsPrev > 0 && (
              <>, {trendData.summary.trend === 'increasing' ? 'up' : 'down'} from{' '}
                {totalDetectionsPrev.toLocaleString()} in the previous period</>
            )}
            . Peak activity: {trendData.summary.peak_period
              ? format(new Date(trendData.summary.peak_period), 'MMM d, HH:mm')
              : 'N/A'}.
          </p>
        </div>
      )}

      {/* Activity Timeline */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-display font-semibold text-white mb-4">
          Detection Timeline
        </h3>
        {timelineData.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineData}>
                <defs>
                  <linearGradient id="aGradAccent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffb547" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#ffb547" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="aGradSky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5fd5ff" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#5fd5ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="time"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7d97', fontSize: 11 }}
                  interval={timeRange === '24h' ? 3 : undefined}
                />
                {/* Left axis = track count; right axis = altitude. Two series
                    on one axis caused altitude (~400 ft) to visually crush
                    tracks (~0–20) into a flat line at the bottom. */}
                <YAxis
                  yAxisId="tracks"
                  orientation="left"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#ffb547', fontSize: 11 }}
                  width={35}
                  allowDecimals={false}
                />
                <YAxis
                  yAxisId="altitude"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#5fd5ff', fontSize: 11 }}
                  width={45}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#142a4a',
                    border: '1px solid #2c4567',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '12px',
                  }}
                />
                <Area
                  yAxisId="tracks"
                  type="monotone"
                  dataKey="tracks"
                  stroke="#ffb547"
                  fill="url(#aGradAccent)"
                  strokeWidth={2}
                  name="Unique Tracks"
                />
                <Area
                  yAxisId="altitude"
                  type="monotone"
                  dataKey="altitude"
                  stroke="#5fd5ff"
                  fill="url(#aGradSky)"
                  strokeWidth={2}
                  name={`Avg Altitude (${altitudeUnit(unitSystem)})`}
                  connectNulls={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500 text-sm">
            {trendLoading ? 'Loading timeline data...' : 'No timeline data available for this period'}
          </div>
        )}
      </div>

      {/* By-Make Breakdown + Donut. Phase 8 replaced "by classification"
          with "by manufacturer" — operators care which brands they're
          seeing (DJI, Autel, Skydio, Parrot, etc.) more than the abstract
          classification taxonomy. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar chart */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-display font-semibold text-white mb-4">
            Detections by Make
          </h3>
          <div className="space-y-3">
            {(() => {
              const makeCounts = byMake ?? {};
              const total = Object.values(makeCounts).reduce((s, n) => s + n, 0);
              const sorted = Object.entries(makeCounts).sort(([, a], [, b]) => b - a);
              if (sorted.length === 0) {
                return (
                  <p className="text-center text-gray-500 py-4">
                    No detections in selected time range
                  </p>
                );
              }
              return sorted.map(([make, count]) => {
                const percentage = total > 0 ? (count / total) * 100 : 0;
                const color = colorForMake(make);
                return (
                  <div key={make}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-sm text-gray-300">{make}</span>
                      </div>
                      <span className="text-sm text-white font-medium tabular-nums">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-navy-700 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all duration-700"
                        style={{ width: `${percentage}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Donut chart */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-display font-semibold text-white mb-4">
            Make Distribution
          </h3>
          {pieData.length > 0 ? (
            <div className="flex items-center justify-center h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#142a4a',
                      border: '1px solid #2c4567',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '12px',
                    }}
                    formatter={(value: number, name: string) => [
                      `${value} detections`,
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-2 justify-center">
            {pieData.slice(0, 6).map((item) => (
              <div key={item.name} className="flex items-center gap-1.5">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-[11px] text-gray-400">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
